import {
  EmailAuthProvider,
  GoogleAuthProvider,
  PhoneAuthProvider,
  linkWithPopup,
  signInWithCredential,
  linkWithCredential,
} from 'firebase/auth';
import { auth } from '../firebase';
import { userHasPhoneCredential } from './authPhone';

const PENDING_GOOGLE_KEY = 'superowl_pending_google_cred';
const PENDING_EMAIL_KEY = 'superowl_pending_email_cred';

/** Persist Google OAuth tokens so we can link them after phone verification. */
export function storePendingGoogleCredential(signInResult) {
  const cred = GoogleAuthProvider.credentialFromResult(signInResult);
  if (!cred) return;
  sessionStorage.setItem(
    PENDING_GOOGLE_KEY,
    JSON.stringify({
      idToken: cred.idToken || null,
      accessToken: cred.accessToken || null,
    }),
  );
}

export function storePendingGoogleOAuthCredential(oauthCredential) {
  if (!oauthCredential) return;
  sessionStorage.setItem(
    PENDING_GOOGLE_KEY,
    JSON.stringify({
      idToken: oauthCredential.idToken || null,
      accessToken: oauthCredential.accessToken || null,
    }),
  );
}

export function getPendingGoogleCredential() {
  const raw = sessionStorage.getItem(PENDING_GOOGLE_KEY);
  if (!raw) return null;
  try {
    const { idToken, accessToken } = JSON.parse(raw);
    if (!idToken && !accessToken) return null;
    return GoogleAuthProvider.credential(idToken, accessToken);
  } catch (_) {
    return null;
  }
}

export function clearPendingGoogleCredential() {
  sessionStorage.removeItem(PENDING_GOOGLE_KEY);
}

/** Persist email/password briefly so we can link to an existing phone account. */
export function storePendingEmailCredential(email, password) {
  if (!email || !password) return;
  sessionStorage.setItem(
    PENDING_EMAIL_KEY,
    JSON.stringify({ email, password }),
  );
}

export function getPendingEmailCredential() {
  const raw = sessionStorage.getItem(PENDING_EMAIL_KEY);
  if (!raw) return null;
  try {
    const { email, password } = JSON.parse(raw);
    if (!email || !password) return null;
    return EmailAuthProvider.credential(email, password);
  } catch (_) {
    return null;
  }
}

export function clearPendingEmailCredential() {
  sessionStorage.removeItem(PENDING_EMAIL_KEY);
}

export function clearAllPendingLinkCredentials() {
  clearPendingGoogleCredential();
  clearPendingEmailCredential();
}

export function userHasGoogleCredential(user) {
  if (!user) return false;
  return (user.providerData || []).some((p) => p.providerId === 'google.com');
}

export function userHasEmailPasswordCredential(user) {
  if (!user) return false;
  return (user.providerData || []).some((p) => p.providerId === 'password');
}

/** Phone-only accounts (typical mobile app users on web) should link Gmail for a second sign-in method. */
export function userNeedsGoogleLink(user) {
  if (!user) return false;
  if (!userHasPhoneCredential(user)) return false;
  if (userHasGoogleCredential(user)) return false;
  if (userHasEmailPasswordCredential(user)) return false;
  return true;
}

const PHONE_EXISTS_CODES = new Set([
  'auth/credential-already-in-use',
  'auth/account-exists-with-different-credential',
  'auth/phone-number-already-exists',
]);

export function isPhoneAlreadyRegisteredError(err) {
  return err && PHONE_EXISTS_CODES.has(err.code);
}

const LINKABLE_CONFLICT_CODES = new Set([
  'auth/provider-already-linked',
  'auth/credential-already-in-use',
  'auth/email-already-in-use',
]);

/** Attach Google to the current (phone) user — popup fallback when tokens are tied to another uid. */
export async function linkPendingGoogleToUser(user) {
  if (!user) return;

  const googleCredential = getPendingGoogleCredential();
  if (googleCredential) {
    try {
      await linkWithCredential(user, googleCredential);
      clearPendingGoogleCredential();
      return;
    } catch (err) {
      if (!LINKABLE_CONFLICT_CODES.has(err.code)) {
        throw err;
      }
    }
  }

  if (userHasGoogleCredential(user)) {
    clearPendingGoogleCredential();
    return;
  }

  const provider = new GoogleAuthProvider();
  await linkWithPopup(user, provider);
  clearPendingGoogleCredential();
}

/** Link Google to the signed-in user via OAuth popup (post phone sign-in gate). */
export async function linkGoogleWithPopup(user) {
  if (!user) {
    throw new Error('Not signed in');
  }
  if (userHasGoogleCredential(user)) {
    return user;
  }

  const provider = new GoogleAuthProvider();
  await linkWithPopup(user, provider);
  clearPendingGoogleCredential();
  await user.reload();
  return user;
}

/**
 * Phone number already belongs to another Firebase account (usually the mobile app).
 * Sign into that phone account and attach any pending Google/email credentials.
 */
export async function mergeExistingPhoneAccount(verificationId, otpCode) {
  const phoneCredential = PhoneAuthProvider.credential(verificationId, otpCode.trim());
  const phoneResult = await signInWithCredential(auth, phoneCredential);

  if (getPendingGoogleCredential()) {
    await linkPendingGoogleToUser(phoneResult.user);
  }

  const emailCredential = getPendingEmailCredential();
  if (emailCredential) {
    try {
      await linkWithCredential(phoneResult.user, emailCredential);
    } catch (err) {
      if (
        err.code !== 'auth/provider-already-linked' &&
        err.code !== 'auth/email-already-in-use' &&
        err.code !== 'auth/credential-already-in-use'
      ) {
        throw err;
      }
    }
    clearPendingEmailCredential();
  }

  await phoneResult.user.reload();
  return phoneResult.user;
}

/** @deprecated Use mergeExistingPhoneAccount */
export async function mergePhoneAccountWithPendingGoogle(verificationId, otpCode) {
  return mergeExistingPhoneAccount(verificationId, otpCode);
}
